import React, {memo, useCallback, useEffect, useMemo, useRef} from 'react'
import {Box, CircularProgress} from "@mui/material";
import {VariableSizeList} from 'react-window';
import AutoSizer from "react-virtualized-auto-sizer";

import {
    add,
    differenceInMonths,
    eachMonthOfInterval,
    getMonth,
    getYear,
    isSaturday,
    isToday,
    startOfDay,
    differenceInDays,
    getDaysInMonth,
    isSunday,
    max,
    min,
    formatISO,
} from "date-fns";

import {
    months,
    dayLabels,
    getSegments,
    sortEvents,
    timeLineVisibleDays,
    monthRange
} from '../utils';

import './timeline.css'
import {usePlanner} from "../index";
import useSelector from "./useSelector";
import Stack from "@mui/material/Stack";

const defaultDayCellWidth = 32;
const segmentHeight = 32;
const emptyArray = [];  // to avoid re-rendering when passing an empty array for events or dataGrid in the month

/*
 This hook behaves like a useRef but allow to specify a setup function and a cleanup function
 that work directly on the ref. Whenever you need to use useEffect on the ref you can use
 this hook instead. (useEffect on ref doesn't work)
 */
const useRefCallback = ({initialValue = null, setup = (r) => r, cleanup = (r) => r}) => {
    const ref = useRef(initialValue);

    return useCallback(node => {
        if (ref.current) {
            cleanup(ref.current);
        }
        if (node) {
            setup(node);
        }

        // Save a reference to the node
        ref.current = node
    }, [])  // eslint-disable-line react-hooks/exhaustive-deps
}

/*
 * The outer component that wrap all the elements: week day names and list of months.
 * Its main purpose is to wrap the virtualized list of months.
 */
const TimelineView = function ({dayCellWidth=defaultDayCellWidth}) {
    const {minDate, maxDate, initialDate, resources, MonthDataLoader, setGoToDate} = usePlanner();
    const initialMonthIndex = differenceInMonths(initialDate, minDate);

    // Reference to the virtualized list itself not to the dom element
    const virtualizedListRef = useRef();
    const selectableAreaRef = useRef();

    useSelector(selectableAreaRef);

    // Reference to the outer dom element of the virtualized list
    const virtualizedListOuterRef = useRefCallback({ // Allow adding listener to the virtualized list
        setup: (node) => node.addEventListener('wheel', handleOnWheel, {passive: false}),
        cleanup: (node) => node.removeEventListener('wheel', handleOnWheel, {passive: false})
    });

    const monthList = useMemo(
        () => eachMonthOfInterval({start: minDate, end: maxDate}),
        [minDate, maxDate]
    );

    /*
     * This function MUST be either memoized or defined outside this component because of the following reasons:
     * - this function is passed as a prop to the virtualized list, so every time the parent re-render
     *   the virtualized list will re-render when the children change.
     * - when the virtualized list rerender if the children (this function) is changed the list re-create all the items,
     *   so each month loader will be un-mounted and remounted.
     * The function cannot be defined outside the component because it uses the month list.
     *
     * The Month prop MUST be a component and not a function that return a component otherwise new months will be
     * created every time the DataLoader re-render. (not sure of that if might also be a render function, but then
     * the render month should be update when the render function change)
     */
    const renderMonth = useCallback(
        ({index, style}) => {
            const month = getMonth(monthList[index]);
            const year = getYear(monthList[index]);
            const [dateStart, dateEnd] = monthRange(month, year);
            return <MonthDataLoader month={month}
                                    year={year}
                                    dateStart={dateStart}
                                    dateEnd={dateEnd}
                                    style={style}
                                    Month={Month}
            />
        },
        [monthList]
    );

    const getMonthWidth = useCallback(
        (index) => getDaysInMonth(monthList[index]) * dayCellWidth,
        [monthList, dayCellWidth]
    );

    // Compute the average month width of all the months from minDate to initialDate to get the correct initial offset
    const averageMonthWidth = useMemo(
        () => {
            const nMonths = differenceInMonths(initialDate, minDate);
            if(nMonths === 0){
                return dayCellWidth * 30.4 // Une the average number of days in a month 365 / 12
            }else{
                return differenceInDays(initialDate, minDate) * dayCellWidth / nMonths
            }
        },
        [minDate, initialDate]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    function handleOnWheel(e) {
        e.preventDefault();
        const {deltaY} = e;
        const current_offset = virtualizedListRef.current.state.scrollOffset;
        virtualizedListRef.current.scrollTo(current_offset + deltaY);
    }

    // Set the goToDate function to the planner context
    useEffect(
        () => {
            const handleSelectMonth = (date) => {
                if (!!virtualizedListRef.current) {
                    const itemIndex = differenceInMonths(max([min([date, maxDate]), minDate]), minDate);
                    virtualizedListRef.current.scrollToItem(itemIndex, "center");
                }
            }
            setGoToDate(() => handleSelectMonth)
        },
        []  // eslint-disable-line react-hooks/exhaustive-deps
    );

    return <Box sx={{display: 'flex', flexDirection: 'row', width: "100%",  mb: '48px'}}>

        <Stack direction="column">
            {
                resources.filter(r => !r.hidden).map(resource => (
                    <Stack key={resource.resourceId}
                           direction="column"
                           justifyContent="center"
                           className={`tl-resource-label${resource.size==="small" ? ' tl-resource-label-small' : ''}`}>
                        {resource.renderLabel()}
                    </Stack>
                ))
            }
        </Stack>

        <Box sx={{flexGrow: 1}} ref={selectableAreaRef} className="tl-select-area">
            <AutoSizer>
                {({height, width}) => {
                    return <VariableSizeList
                        ref={virtualizedListRef}
                        outerRef={virtualizedListOuterRef}
                        layout="horizontal"
                        width={width}
                        height={height + 15}  // 15 for the scrollbar
                        itemCount={monthList.length}
                        overscanCount={2}
                        estimatedItemSize={averageMonthWidth}
                        itemSize={getMonthWidth}
                        initialScrollOffset={initialMonthIndex * averageMonthWidth}
                        style={{'overflow': 'auto hidden'}}
                    >
                        {renderMonth}
                    </VariableSizeList>
                }}

            </AutoSizer>
        </Box>
    </Box>;
}

/*
 * Month component: it renders the month header the grid and the events. This is not memoized because its rendering
 * time is not significant, but its children (header, grid, and content) are memoized for performance reason.
 */
const Month = function (props) {
    const {month, year, contentData, gridData, isLoading, style} = props;
    const {resources, selectedRange} = usePlanner();
    const days = useMemo(() => timeLineVisibleDays(month, year), [month, year]);

    const resourceClass = (r) => {
        let className = 'tl-resource-content';
        if (r.size === 'small') {
            className += ' tl-resource-content-small';
        }
        if (r.resourceId.toString() === selectedRange.resource) {
            className += ' tl-resource-selected';
        }
        return className;
    }

    const getContentData = (resourceId) => {
        if (!!contentData && !!contentData[resourceId]) {
            return contentData[resourceId];
        }else{
            return emptyArray;
        }
    }

    return <div className="tl-month" style={style}>
        <MonthHeader month={month} year={year} days={days} isLoading={isLoading}/>
        {
            resources.filter(r => !r.hidden).map(resource => (
                <div key={resource.resourceId.toString()}
                     className={resourceClass(resource)}
                     data-tl-resource={resource.resourceId}>
                    <Grid days={days}
                          renderDayCell={resource.renderDayCell}
                          gridData={gridData}
                          selectedRange={selectedRange}/>
                    <Content days={days}
                             events={getContentData(resource.resourceId)}/>
                </div>
            ))
        }
    </div>
}

/*
 * The header of a month.
 * memo prevent the re-rendering of the component when the parent is re-rendered, e.g when the user scroll the timeline
 */
const MonthHeader = memo(function (props) {
    const {month, year, days, isLoading} = props;

    return <div className={`tl-month-header ${getMonth(new Date(year, month, 1)) % 2 === 0 ? '' : 'tl-grid-bg-grey'}`}>
        <div className="tl-month-label">
            {`${months[month].name} ${year}`}
            <div className={"tl-month-label-spinner"}>
                {isLoading && <CircularProgress size={14} color="primary"/>}
            </div>
        </div>
        <div className="tl-month-days">
            {
                days.map((day, i) => <DateNumber key={i}
                                                 dayNumber={day.getDate()}
                                                 dayLabel={dayLabels[day.getDay()]}
                                                 isToday={isToday(day)}
                                                 isRed={isSunday(day)}/>)
            }
        </div>
    </div>
})

// Day number
const DateNumber = memo(function (props) {
    const {dayLabel, dayNumber, isToday, isRed} = props;

    return <div className='tl-day-cell'>
        <span className={`tl-day-number ${isRed ? 'tl-day-number-sunday' : ''}`}>{dayLabel}</span>
        <span
            className={`tl-day-number ${isRed ? 'tl-day-number-sunday' : ''} ${isToday ? 'tl-day-number-today' : ''}`}>
            {dayNumber}
        </span>
    </div>
})

/*
 * The grid of a single month.
 * memo prevent the re-rendering of the component when the parent is re-rendered, e.g when the user scroll the timeline
 */
const Grid = memo(function (props) {
    // I'm passing selectedRange as props to avoid re-render when the selectedRange is changed
    // (when the Context is re-rendered)
    const {days, renderDayCell, gridData, selectedRange} = props;

    const dayClasses = (d) => {
        let classes = 'tl-day-grid';
        const dateISO = formatISO(d, {representation: 'date'});

        if (getMonth(d) % 2 !== 0) {
            classes += ' tl-grid-bg-grey'
        }
        if (isSaturday(d)) {
            classes += ' tl-grid-bg-highlighted'
        }
        if (selectedRange.from <= dateISO && dateISO <= selectedRange.to) {
            classes += ' tl-grid-selected'
        }
        return classes;
    }

    return <div className='tl-row-grid'>
        {
            days.map((d, i) => {
                const dateISO = formatISO(d, {representation: 'date'});
                return <div key={i}
                            className={dayClasses(d)}
                            data-tl-dts={dateISO}>
                    {renderDayCell && renderDayCell(dateISO, gridData)}
                </div>
            })
        }
    </div>
})

// The content of a month
const Content = memo(function (props) {
    const {days, events} = props;
    const firstDate = startOfDay(days[0]);
    const lastDate = startOfDay(add(days[days.length - 1], {days: 1}));
    const segments = getSegments(sortEvents(events), firstDate, lastDate, false);

    return <div className="tl-content">
        <div className='tl-row'>
            {segments.map(s => <RowSegment key={s.segmentId} segment={s}/>)}
        </div>
    </div>
})

// A segment that may span over more days containing either an event or representing just an offset
const RowSegment = function (props) {
    const {segment} = props;
    const {renderEvent} = usePlanner();

    const width = 100.0 * segment.lengthFraction;

    return <div style={{
        flexBasis: `${width}%`,
        maxWidth: `${width}%`,
        height: `${segmentHeight}px`,
        maxHeight: `${segmentHeight}px`,
        overflow: 'hidden',
        flexShrink: 0,
    }}>
        {!!segment.event && renderEvent(segment.event, segment)}
    </div>
}

export default TimelineView;